import { ActionIcon, Group, Paper, Popover, Slider, Text, Tooltip } from '@mantine/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaDownload, FaPause, FaPlay, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

interface RichAudioPlayerProps {
    src: string;
    title?: string;
    autoPlay?: boolean;
    persistedVolume?: number;
    persistedSpeed?: number;
    onVolumeChange?: (volume: number) => void;
    onSpeedChange?: (speed: number) => void;
    onPlayStateChange?: (isPlaying: boolean) => void;
}

const SPEED_VALUES = [0.5, 1, 1.5, 2];

const formatTime = (value: number) => {
    if (!Number.isFinite(value) || value < 0) return '00:00';
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default function RichAudioPlayer({
    src,
    title = 'Call recording',
    autoPlay = false,
    persistedVolume = 100,
    persistedSpeed = 1,
    onVolumeChange,
    onSpeedChange,
    onPlayStateChange,
}: RichAudioPlayerProps) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(persistedVolume);
    const [speed, setSpeed] = useState(persistedSpeed);
    const [showVolumeControl, setShowVolumeControl] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.pause();
        audio.currentTime = 0;
        audio.playbackRate = speed;
        audio.volume = volume / 100;

        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setShowVolumeControl(false);
        onPlayStateChange?.(false);
    }, [src]);

    useEffect(() => {
        setVolume(persistedVolume);
    }, [persistedVolume]);

    useEffect(() => {
        setSpeed(persistedSpeed);
    }, [persistedSpeed]);

    useEffect(() => {
        onPlayStateChange?.(isPlaying);
    }, [isPlaying, onPlayStateChange]);

    useEffect(() => {
        if (!autoPlay) return;
        const audio = audioRef.current;
        if (!audio) return;

        audio.play()
            .then(() => setIsPlaying(true))
            .catch(() => {
                setIsPlaying(false);
                onPlayStateChange?.(false);
            });
    }, [src, autoPlay]);

    const progress = useMemo(() => {
        if (!duration) return 0;
        return Math.min(100, (currentTime / duration) * 100);
    }, [currentTime, duration]);

    const handleTogglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }

        audio.play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
    };

    const handleSeek = (value: number) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;

        const nextTime = (value / 100) * duration;
        audio.currentTime = nextTime;
        setCurrentTime(nextTime);
    };

    const handleVolume = (value: number) => {
        const audio = audioRef.current;
        if (!audio) return;

        const normalizedVolume = value / 100;
        audio.volume = normalizedVolume;
        setVolume(value);
        onVolumeChange?.(value);
    };

    const handleSpeedCycle = () => {
        const audio = audioRef.current;
        if (!audio) return;

        const currentSpeedIndex = SPEED_VALUES.findIndex((value) => value === speed);
        const nextSpeed = SPEED_VALUES[(currentSpeedIndex + 1) % SPEED_VALUES.length];
        audio.playbackRate = nextSpeed;
        setSpeed(nextSpeed);
        onSpeedChange?.(nextSpeed);
    };

    return (
        <Paper
            mt="lg"
            p="md"
            radius="md"
            style={{
                border: '1px solid rgba(120, 133, 163, 0.25)',
                background: 'linear-gradient(145deg, rgba(16,24,40,0.04), rgba(56,189,248,0.08))',
            }}
        >
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                    onPlayStateChange?.(false);
                }}
            />

            <Group justify="space-between" align="center" mb="xs" wrap="wrap">
                <Group>
                    <ActionIcon variant="filled" color="blue" size="lg" radius="xl" onClick={handleTogglePlay}>
                        {isPlaying ? <FaPause /> : <FaPlay />}
                    </ActionIcon>
                    <Text fw={600} size="sm">{title}</Text>
                </Group>

                <Group gap="xs" align="center">
                    <ActionIcon
                        component="a"
                        href={src}
                        download
                        target="_blank"
                        rel="noreferrer"
                        variant="light"
                        color="dark"
                        size="lg"
                        radius="xl"
                        title="Download"
                    >
                        <FaDownload />
                    </ActionIcon>

                    <Popover
                        opened={showVolumeControl}
                        onChange={setShowVolumeControl}
                        position="top-end"
                        shadow="md"
                        withArrow
                        withinPortal={false}
                    >
                        <Popover.Target>
                            <ActionIcon
                                variant="light"
                                color="dark"
                                size="lg"
                                radius="xl"
                                title="Volume"
                                onClick={() => setShowVolumeControl(v => !v)}
                            >
                                {volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                            </ActionIcon>
                        </Popover.Target>

                        <Popover.Dropdown
                            p="xs"
                            style={{
                                border: '1px solid rgba(120, 133, 163, 0.25)',
                                minWidth: 0,
                                width: 48,
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={volume}
                                onChange={(event) => handleVolume(Number(event.currentTarget.value))}
                                aria-label="Volume"
                                style={{
                                    width: 8,
                                    height: 120,
                                    writingMode: 'vertical-lr',
                                    direction: 'rtl',
                                    WebkitAppearance: 'slider-vertical',
                                    cursor: 'pointer',
                                }}
                            />
                        </Popover.Dropdown>
                    </Popover>

                    <Tooltip label="Change speed (+0.5x)">
                        <ActionIcon
                            variant="filled"
                            color="dark"
                            size="lg"
                            radius="xl"
                            onClick={handleSpeedCycle}
                            title="Playback speed"
                        >
                            <Text size="xs" fw={700} c="white">{speed}x</Text>
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>

            <Slider
                value={progress}
                onChange={handleSeek}
                size="sm"
                color="blue"
                label={null}
                disabled={!duration}
            />

            <Group justify="space-between" mt="xs" mb="sm">
                <Text size="xs" c="dimmed">{formatTime(currentTime)}</Text>
                <Text size="xs" c="dimmed">{formatTime(duration)}</Text>
            </Group>
        </Paper>
    );
}
